import {
  displayPayslipTitle,
  displayRunName,
  localizeMonthLabel,
} from "@/utils/payroll-names";
import { useLocale } from "@/hooks/useLocale";
import { useState } from "react";
import { useNavigate, useParams, useMatch } from "react-router-dom";
import { ChevronDown, ChevronLeft, ChevronUp, Download } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SettlementDocument } from "../_components/SettlementDocument";
import { SettlementPaymentActions } from "./_components/SettlementPaymentActions";
import { ROUTES } from "@/constants/routes";
import { COMPANY_OFFBOARDING_PATHS } from "@/routes/company/routes.offboarding";
import { usePageTitle } from "@/hooks/usePageTitle";
import { withBranding } from "@/utils/helper";
import { useGetFinalSettlementDetailQuery } from "@/services/(company)/payrollRunsApi";
import { useDownloadFinalSettlement } from "@/services/(company)/finalSettlementDownload";
import { useTranslation } from "react-i18next";

// ─── Helpers (same shape as offboarding FinalSettlementTab) ───────────────────

const fmt = (n: number) =>
  n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const FormLabel = ({ text }: { text: string }) => (
  <div className="text-xs text-[#7B7B7B] font-medium uppercase">{text}</div>
);

// ─── Collapsible Section (copied from offboarding tab) ────────────────────────

const Section = ({
  title,
  badge,
  children,
}: {
  title: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-gray-200 rounded-sxs">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="flex w-full items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors rounded-sxs"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">{title}</span>
          {badge}
        </div>
        {open ? (
          <ChevronUp className="size-4 text-gray-400" />
        ) : (
          <ChevronDown className="size-4 text-gray-400" />
        )}
      </button>
      {open && (
        <div className="border-t border-gray-100 px-4 py-4 overflow-visible bg-white">
          {children}
        </div>
      )}
    </div>
  );
};

// ─── Line Items (read-only version mirrors offboarding submitted state) ──────

type LineItem = { name: string; amount: number; isActive: boolean };

const LineItems = ({
  items,
  accentColor,
}: {
  items: LineItem[];
  accentColor: "green" | "red";
}) => {
  const { t } = useTranslation(["company", "common"]);
  const dotCls = accentColor === "green" ? "bg-green-500" : "bg-red-500";
  return (
    <div className="flex flex-col gap-2">
      {items.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2.5">
          <span
            className={`size-2 rounded-full shrink-0 ${dotCls} ${!item.isActive ? "opacity-30" : ""}`}
          />
          <Input
            type="text"
            value={item.name}
            disabled
            placeholder={t("company:payrolls.description")}
            className="flex-1 h-9"
          />
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-xs text-gray-400 font-medium">AED</span>
            <Input
              type="number"
              value={item.amount}
              disabled
              min={0}
              className="w-28 h-9 text-end"
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────

export const FinalSettlementDetail = () => {
  const { t } = useTranslation(["company", "common"]);
  const { formatDate: localeDate } = useLocale();
  usePageTitle(withBranding(t("company:payrolls.final_settlement_detail")));
  const navigate = useNavigate();
  const fromOffboarding = !!useMatch(
    "/company/offboarding/final-settlements/:id",
  );
  const { id } = useParams<{ id: string }>();
  const settlementId = Number(id);
  const skip = !settlementId || isNaN(settlementId);

  const {
    data: response,
    isLoading,
    refetch,
  } = useGetFinalSettlementDetailQuery(settlementId, { skip });
  const detail = response?.data;
  const { download, isDownloading } = useDownloadFinalSettlement();

  const handleBack = () =>
    fromOffboarding
      ? navigate(COMPANY_OFFBOARDING_PATHS.FINAL_SETTLEMENTS)
      : navigate(ROUTES.HOME.COMPANY.PAYROLL.FINAL_SETTLEMENTS);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="size-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="p-6 flex flex-col gap-3 items-start">
        <span className="text-sm text-gray-500">
          {t("company:payrolls.settlement_not_found")}
        </span>
        <Button variant="outline" onClick={handleBack}>
          {t("common:buttons.back")}
        </Button>
      </div>
    );
  }

  // Paid means the item was paid, never that its run completed: building the
  // WPS file completes a settlement run long before the bank pays it, and
  // reading run status showed the leaver's gratuity as "Paid" while hiding
  // the only button that could record the real payment.
  const isPaid = detail.paid || !!detail.paidAt;
  const employee = detail.header.employee;
  const ccy =
    detail.netPay.currency?.code ??
    detail.paymentDetails.currency?.code ??
    "AED";

  const earnings: LineItem[] = (detail.payslipDetail.earnings ?? []).map(
    (e) => ({
      name: e.name,
      amount: Number(e.amount || 0),
      isActive: true,
    }),
  );
  const deductions: LineItem[] = (detail.payslipDetail.deductions ?? []).map(
    (d) => ({
      name: d.name,
      amount: Number(d.amount || 0),
      isActive: true,
    }),
  );
  const totalEarnings = earnings.reduce((s, e) => s + e.amount, 0);
  const totalDeductions = deductions.reduce((s, d) => s + d.amount, 0);
  const statutoryRows = detail.payslipDetail.statutoryRows ?? [];
  const totalStatutoryEmployee = Number(
    detail.payslipDetail.totalStatutoryEmployee ?? 0,
  );
  const totalStatutoryEmployer = Number(
    detail.payslipDetail.totalStatutoryEmployer ?? 0,
  );
  const hasStatutory = statutoryRows.length > 0;

  const paymentMethod = detail.paymentDetails.paymentMethod ?? "";
  const bankName = detail.paymentDetails.bankName ?? "";
  const accountNumber =
    detail.paymentDetails.accountMaskedWithBank ??
    detail.paymentDetails.accountMasked ??
    "";
  const needsBankDetails =
    paymentMethod === "bank_transfer" || paymentMethod === "cheque";

  return (
    <div className="flex flex-col gap-0 bg-background-tertiary">
      {/* Top header (back + download) — outside offboarding because no parent detail page */}
      <div className="flex items-center justify-between gap-3 border-b border-borderSecondary bg-white px-6 py-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={handleBack}
            className="border border-surface-elevated rounded-xs p-2 w-fit h-fit shrink-0"
          >
            <ChevronLeft className="text-primary size-5 rtl:rotate-180" />
          </button>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold text-gray-900">
              {displayRunName(
                t,
                displayPayslipTitle(t, detail.title, localeDate),
                localeDate,
              )}
            </span>
            <span className="text-xs text-gray-400">
              {detail.runCode} ·{" "}
              {localizeMonthLabel(detail.header.periodLabel, localeDate)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {fromOffboarding ? (
            <Badge variant={isPaid ? "success" : "warning"}>
              {isPaid
                ? t("company:payrolls.paid")
                : t("company:payrolls.pending_payment")}
            </Badge>
          ) : (
            <SettlementPaymentActions
              itemId={detail.id}
              runId={detail.runId}
              runStatus={detail.runStatus}
              paymentMethod={paymentMethod}
              hasPaymentFile={Boolean(detail.hasPaymentFile)}
              isPaid={isPaid}
              onChanged={refetch}
            />
          )}
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="gap-1.5"
            disabled={!detail.payslipGeneratable || isDownloading}
            onClick={() => download(detail.id)}
          >
            <Download size={14} />
            {isDownloading
              ? t("common:states.downloading")
              : t("common:buttons.download")}
          </Button>
        </div>
      </div>

      {/* Body — identical layout to offboarding FinalSettlementTab */}
      <div className="flex gap-6 p-6">
        {/* ── Left: Form ── */}
        <div className="flex-1 flex flex-col gap-4 min-w-0">
          <div>
            <h2 className="text-sm font-semibold text-gray-800">
              {t("company:payrolls.final_settlement")}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {t(
                "company:payrolls.view_earnings_deductions_and_statutory_contributions_for",
              )}
            </p>
          </div>

          {/* Payment Details */}
          <Section title={t("company:payrolls.payment_details")}>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <FormLabel text={t("company:payrolls.payment_method")} />
                <Input
                  type="text"
                  value={
                    paymentMethod
                      ? // Stored as `WPS`; the keys are lower case (B26).
                        t(
                          `common:payment_methods.${paymentMethod.toLowerCase()}`,
                          {
                            defaultValue: paymentMethod,
                          },
                        )
                      : "—"
                  }
                  disabled
                />
              </div>

              <div className="flex flex-col gap-2">
                <FormLabel text={t("company:payrolls.currency")} />
                <Input type="text" value={ccy} disabled placeholder="AED" />
              </div>

              {needsBankDetails && (
                <>
                  <div className="flex flex-col gap-2">
                    <FormLabel text={t("company:payrolls.bank_name")} />
                    <Input
                      type="text"
                      value={bankName || "—"}
                      disabled
                      placeholder={t("company:payrolls.emirates_nbd")}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <FormLabel
                      text={
                        paymentMethod === "cheque"
                          ? t("company:payrolls.cheque_number")
                          : t("company:payrolls.account_number")
                      }
                    />
                    <Input
                      type="text"
                      value={accountNumber || "—"}
                      disabled
                      placeholder={
                        paymentMethod === "cheque"
                          ? t("company:payrolls.cheque_no")
                          : t("company:payrolls.iban_account_no")
                      }
                    />
                  </div>
                </>
              )}
            </div>
          </Section>

          {/* Earnings */}
          <Section
            title={t("company:payrolls.earnings")}
            badge={
              earnings.length > 0 ? (
                <span className="text-xs font-bold text-green-600 ms-1">
                  {ccy} {fmt(totalEarnings)}
                </span>
              ) : undefined
            }
          >
            <LineItems items={earnings} accentColor="green" />
            {earnings.length > 0 && (
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
                <span className="text-xs text-gray-500">
                  {t("company:payrolls.total_earnings")}
                </span>
                <span className="text-sm font-bold text-gray-800">
                  {ccy} {fmt(totalEarnings)}
                </span>
              </div>
            )}
          </Section>

          {/* Deductions */}
          <Section
            title={t("company:payrolls.deductions")}
            badge={
              deductions.length > 0 ? (
                <span className="text-xs font-bold text-red-500 ms-1">
                  {ccy} {fmt(totalDeductions)}
                </span>
              ) : undefined
            }
          >
            <LineItems items={deductions} accentColor="red" />
            {deductions.length > 0 && (
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
                <span className="text-xs text-gray-500">
                  {t("company:payrolls.total_deductions")}
                </span>
                <span className="text-sm font-bold text-red-500">
                  {ccy} {fmt(totalDeductions)}
                </span>
              </div>
            )}
          </Section>

          {/* Statutory Contributions */}
          {hasStatutory && (
            <Section
              title={t("company:payrolls.statutory_contributions")}
              badge={
                <Badge className="bg-gray-100 text-gray-400 ms-1">
                  {t("company:payrolls.auto_calculated")}
                </Badge>
              }
            >
              <div className="flex flex-col gap-3 text-sm">
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 px-3 py-2 rounded-sxs">
                  {t("company:payrolls.auto_calculated_from_country_rule")}
                </p>
                {statutoryRows.some((r) => r.employeeAmt > 0) && (
                  <div className="flex flex-col gap-2">
                    <p className="text-xs font-medium uppercase text-[#7B7B7B]">
                      {t("common:labels.employee")}
                    </p>
                    {statutoryRows
                      .filter((r) => r.employeeAmt > 0)
                      .map((r, i) => (
                        <div
                          key={`emp-${i}`}
                          className="flex items-start justify-between"
                        >
                          <div>
                            <p className="font-medium text-gray-800">
                              {r.label}
                            </p>
                            <p className="text-xs text-gray-400">
                              {r.basis}
                              {r.rate ? ` · ${r.rate}%` : ""}
                            </p>
                          </div>
                          <span className="font-semibold text-red-500">
                            − {ccy} {fmt(r.employeeAmt)}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
                {statutoryRows.some((r) => r.employerAmt > 0) && (
                  <div className="flex flex-col gap-2 pt-2 border-t border-gray-100">
                    <p className="text-xs font-medium uppercase text-[#7B7B7B]">
                      {t("company:payrolls.employer_not_deducted_from_salary")}
                    </p>
                    {statutoryRows
                      .filter((r) => r.employerAmt > 0)
                      .map((r, i) => (
                        <div
                          key={`emr-${i}`}
                          className="flex items-start justify-between"
                        >
                          <div>
                            <p className="font-medium text-gray-800">
                              {r.label}
                            </p>
                            <p className="text-xs text-gray-400">
                              {r.basis}
                              {r.rate ? ` · ${r.rate}%` : ""}
                            </p>
                          </div>
                          <span className="font-semibold text-gray-600">
                            {ccy} {fmt(r.employerAmt)}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
                <div className="border-t border-gray-100 pt-3 flex flex-col gap-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">
                      {t("company:payrolls.total_employee_deductions")}
                    </span>
                    <span className="font-semibold text-red-500">
                      − {ccy} {fmt(totalStatutoryEmployee)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">
                      {t("company:payrolls.total_employer_liability")}
                    </span>
                    <span className="font-semibold text-gray-600">
                      {ccy} {fmt(totalStatutoryEmployer)}
                    </span>
                  </div>
                </div>
              </div>
            </Section>
          )}
        </div>

        {/* ── Right: Document Preview ── */}
        <div className="w-[480px] shrink-0">
          <SettlementDocument
            sticky
            refNo={detail.settlementRefNo ?? detail.runCode}
            company={detail.payslipPreview?.company ?? null}
            // Issued now, like the PDF — not the period ("June 2026", B25).
            issueDate={localeDate(new Date(), "dd MMM yyyy")}
            employee={{
              name: employee.fullName,
              employeeId: employee.employeeCode,
              designation: employee.designation,
              department: detail.header.department?.name ?? null,
              joiningDate: detail.header.joinDate,
              lastWorkingDay: detail.header.lastWorkingDay,
            }}
            payment={{
              method: paymentMethod,
              payDate: detail.paymentDetails.payDate,
              bankName,
              accountNumber: detail.paymentDetails.accountMasked,
            }}
            currency={ccy}
            earnings={earnings.map((e) => ({ name: e.name, amount: e.amount }))}
            deductions={deductions.map((d) => ({
              name: d.name,
              amount: d.amount,
            }))}
            totalEarnings={totalEarnings}
            totalDeductions={totalDeductions}
            statutoryRows={statutoryRows}
            totalStatutoryEmployee={totalStatutoryEmployee}
            totalStatutoryEmployer={totalStatutoryEmployer}
            netSalary={Number(detail.netPay.amount || 0)}
          />
        </div>
      </div>
    </div>
  );
};

export default FinalSettlementDetail;
